import { Sparkles } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Skeleton } from "components/ui/skeleton";

import {
  CAMPAIGN_TYPE_LABELS,
  GamificationStatus,
  type IConcurrencyGroup,
  type IConcurrencyGroupCampaign,
} from "@/@types/gamification";
import { useGetConcurrencyGroupsQuery } from "@/services/gamification";
import { formatDateString } from "@/utils/date-string";

import { ConcurrencyGroupListState } from "../constants/concurrency-group";
import { CONCURRENCY_GROUP_COPY } from "../constants/messages";
import {
  findConcurrencyGroup,
  resolveConcurrencyGroupListState,
} from "../utils/concurrency-group";
import { STATUS_COLORS, STATUS_LABELS } from "../utils/status";
import { mapStatusToFrontend } from "../utils/status-mapper";
import { PriorityBadge } from "./priority-badge";

const HEADING_ID = "concurrency-group-composition-title";

const SKELETON_ROWS = 2;

interface ConcurrencyGroupCompositionProps {
  groupName: string;
  excludeCampaignId?: number;
}

function periodLabel(campaign: IConcurrencyGroupCampaign): string {
  if (!campaign.startDate || !campaign.endDate) {
    return CONCURRENCY_GROUP_COPY.compositionNoPeriod;
  }
  return CONCURRENCY_GROUP_COPY.compositionPeriod(
    formatDateString(campaign.startDate),
    formatDateString(campaign.endDate),
  );
}

function CompositionRow({
  campaign,
}: {
  campaign: IConcurrencyGroupCampaign;
}): JSX.Element {
  const status: GamificationStatus = mapStatusToFrontend(campaign.status ?? "", null);
  const displayName: string =
    campaign.customerFacingName ?? campaign.campaignName ?? String(campaign.id);
  const showInternalName: boolean =
    Boolean(campaign.customerFacingName) && Boolean(campaign.campaignName);

  return (
    <li className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="break-words text-sm font-medium">{displayName}</p>
        {showInternalName && (
          <p className="break-words text-xs text-muted-foreground">
            {CONCURRENCY_GROUP_COPY.compositionInternalName(
              campaign.campaignName ?? "",
            )}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {CONCURRENCY_GROUP_COPY.compositionMeta(
            CAMPAIGN_TYPE_LABELS[campaign.campaignType],
            periodLabel(campaign),
          )}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={STATUS_COLORS[status]}>
          {STATUS_LABELS[status]}
        </Badge>
        <PriorityBadge value={campaign.priority} />
      </div>
    </li>
  );
}

function NewGroupNotice(): JSX.Element {
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-lg border border-dashed p-3 text-sm"
    >
      <Sparkles aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
      <div>
        <p className="font-medium">
          {CONCURRENCY_GROUP_COPY.compositionNewGroupTitle}
        </p>
        <p className="text-muted-foreground">
          {CONCURRENCY_GROUP_COPY.compositionNewGroup}
        </p>
      </div>
    </div>
  );
}

function CompositionList({ group }: { group: IConcurrencyGroup }): JSX.Element {
  return (
    <section aria-labelledby={HEADING_ID} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={HEADING_ID} className="text-sm font-semibold">
          {CONCURRENCY_GROUP_COPY.compositionTitle(group.name)}
        </h3>
        <Badge variant="secondary">
          {CONCURRENCY_GROUP_COPY.countSummary(group.campaignCount, group.activeCount)}
        </Badge>
      </div>
      <ul className="space-y-2">
        {group.campaigns.map((campaign) => (
          <CompositionRow key={campaign.id} campaign={campaign} />
        ))}
      </ul>
    </section>
  );
}

function CompositionLoading(): JSX.Element {
  return (
    <div role="status" aria-label={CONCURRENCY_GROUP_COPY.compositionLoading} className="space-y-2">
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  );
}

export function ConcurrencyGroupComposition({
  groupName,
  excludeCampaignId,
}: ConcurrencyGroupCompositionProps): JSX.Element {
  const { data, isFetching, isError } = useGetConcurrencyGroupsQuery({
    excludeCampaignId,
  });
  const state: ConcurrencyGroupListState = resolveConcurrencyGroupListState({
    isLoading: isFetching,
    isError,
    hasData: data !== undefined,
  });

  if (state === ConcurrencyGroupListState.LOADING) return <CompositionLoading />;
  if (state === ConcurrencyGroupListState.ERROR) {
    return (
      <p role="alert" className="text-sm text-red-600">
        {CONCURRENCY_GROUP_COPY.compositionError}
      </p>
    );
  }

  const group: IConcurrencyGroup | undefined = findConcurrencyGroup(data, groupName);
  if (!group) return <NewGroupNotice />;
  return <CompositionList group={group} />;
}
